/**
 * Rota agaci.
 *
 * ── KORUMA ISTEMCIDE DEGIL ───────────────────────────────────────────────────
 * `RequireAuth` ve `RequireNav` KOLAYLIK bilesenleri: kullaniciyi bos bir
 * ekranda birakmamak icin varlar. Gercek koruma sunucuda, her uc noktada.
 * Birisi URL'yi elle yazip /yonetim'e giderse arayuz cizilir ama API 403 doner.
 *
 * ── TURKCE URL ───────────────────────────────────────────────────────────────
 * Yollar Turkce ("/kitaplik", "/operasyon"). Kullanici adres cubugunu okur;
 * "/library" yazan bir Turkce arayuz yarim kalmis hissi verir.
 */

import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { useAuth } from './lib/auth';
import { LoadingBlock } from './components/ui';
import AppShell from './layouts/AppShell';
import './pages/admin/Admin.css';
import AuthLayout from './layouts/AuthLayout';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import GoogleConsent from './pages/auth/GoogleConsent';
import LegalPage from './pages/auth/LegalPage';
import Invitation from './pages/auth/Invitation';
import WorkforceInvitation from './pages/auth/WorkforceInvitation';

import Home from './pages/Home';
import Library from './pages/Library';
import CategoryPage from './pages/CategoryPage';
import Subscriptions from './pages/Subscriptions';
import Reports from './pages/Reports';
import Support from './pages/Support';
import CalendarBooking from './pages/CalendarBooking';
import OfferDetail from './pages/OfferDetail';
import ProductDetail from './pages/ProductDetail';
import Endpoints from './pages/operations/Endpoints';
import Schedule from './pages/operations/Schedule';
import Account from './pages/account/Account';
import NotFound from './pages/NotFound';
import LedOperations from './pages/LedOperations';
import LedDemo from './pages/LedDemo';
import QuoteRequest from './pages/QuoteRequest';
import PackageFinder from './pages/PackageFinder';
import DisplayRuntimeWorkspace from './pages/operations/DisplayRuntimeWorkspace';

import type { NavKey } from '@shared/index';

// Yönetim konsolu müşterinin günlük Hub açılışını ağırlaştırmaz. Aynı modülde
// yaşayan kardeş ekranlar tek ağ parçasını paylaşır ve ilk ihtiyaçta yüklenir.
const AdminShell = lazy(() => import('./layouts/AdminShell'));
const Overview = lazy(() => import('./pages/admin/tabs/Overview').then((module) => ({ default: module.Overview })));
const Accounts = lazy(() => import('./pages/admin/tabs/Accounts').then((module) => ({ default: module.Accounts })));
const AccountDetailPage = lazy(() => import('./pages/admin/tabs/Accounts').then((module) => ({ default: module.AccountDetailPage })));
const NewCustomerPage = lazy(() => import('./pages/admin/CustomerWorkspace').then(module => ({default:module.NewCustomerPage})));
const CustomerOfferPage = lazy(() => import('./pages/admin/CustomerWorkspace').then(module => ({default:module.CustomerOfferPage})));
const CustomerEventEditor = lazy(() => import('./pages/admin/CustomerEventEditor').then(module => ({default:module.CustomerEventEditor})));
const Products = lazy(() => import('./pages/admin/tabs/Products').then((module) => ({ default: module.Products })));
const Packages = lazy(() => import('./pages/admin/tabs/Packages').then((module) => ({ default: module.Packages })));
const Banners = lazy(() => import('./pages/admin/tabs/Banners').then((module) => ({ default: module.Banners })));
const CategoryContent = lazy(() => import('./pages/admin/tabs/CategoryContent').then((module) => ({ default: module.CategoryContent })));
const Audit = lazy(() => import('./pages/admin/tabs/Audit').then((module) => ({ default: module.Audit })));
const ContentCenter = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.ContentCenter })));
const MediaLibrary = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.MediaLibrary })));
const OperationsCenter = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.OperationsCenter })));
const OperationsSessions = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.OperationsSessions })));
const QuoteRequests = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.QuoteRequests })));
const ReleasesCenter = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.ReleasesCenter })));
const SupportCenter = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.SupportCenter })));
const SystemCenter = lazy(() => import('./pages/admin/tabs/ReferencePanels').then((module) => ({ default: module.SystemCenter })));
const AdminPlatformPages = () => import('./pages/admin/AdminPlatformPages');
const AdminOffers = lazy(() => AdminPlatformPages().then((module) => ({ default: module.AdminOffers })));
const AdminInventory = lazy(() => AdminPlatformPages().then((module) => ({ default: module.AdminInventory })));
const AdminWebsite = lazy(() => AdminPlatformPages().then((module) => ({ default: module.AdminWebsite })));
const AdminWebsiteEditor = lazy(() => AdminPlatformPages().then((module) => ({ default: module.AdminWebsiteEditor })));
const AdminEmails = lazy(() => AdminPlatformPages().then((module) => ({ default: module.AdminEmails })));
const AdminInvoices = lazy(() => import('./pages/admin/AdminInvoices'));
const AdminTasks = lazy(() => import('./pages/admin/AdminTasks'));
const AdminDevAccess = lazy(() => import('./pages/admin/AdminDevAccess'));
const AdminWorkforce = lazy(() => import('./pages/admin/AdminWorkforce'));
const AdminStaffRoles = lazy(() => import('./pages/admin/AdminStaffRoles'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-7)', maxWidth: 720, margin: '0 auto' }}>
        <LoadingBlock rows={4} label="Oturum kontrol ediliyor" />
      </div>
    );
  }

  if (!me) {
    // Nereye gitmek istedigini SAKLIYORUZ: giristen sonra oraya donsun,
    // her seferinde ana sayfaya atmak kullaniciyi yolundan eder.
    return <Navigate to="/giris" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

function RequireNav({ nav, children }: { nav: NavKey; children: ReactNode }) {
  const { can, me } = useAuth();

  /**
   * ── TAVUK-YUMURTA ISTISNASI ────────────────────────────────────────────────
   * `operations` nav'i "en az bir endpoint varsa" acilir. Bu, HENUZ cihazi
   * olmayan musteri yoneticisini kilitliyordu: cihaz eklemek icin Operasyon'a
   * girmesi, Operasyon'un acilmasi icin cihazi olmasi gerekiyordu. Nav bir
   * ONERIDIR (menude gorunsun mu), yetki degil — yetki sunucuda.
   */
  const canManage = me?.user.role === 'owner' || me?.user.role === 'customer_admin';
  if (!can(nav) && !(nav === 'operations' && canManage)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** /kitaplik -> /kesfet — yol ve sorgu dizisi korunarak. */
function LegacyActivationRedirect() {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  search.set('gorunum', 'activation');
  return <Navigate to={`/yonetim/musteriler/yeni?${search}`} replace />;
}

function LegacyLibraryRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={location.pathname.replace('/kitaplik', '/kesfet') + location.search}
      replace
    />
  );
}

/** Eski urun detay linki yeni kanonik Kesfet detayina tasinir. */
function LegacyOwnedContentRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={location.pathname.replace('/iceriklerim', '/kesfet') + location.search}
      replace
    />
  );
}

/**
 * Yonetici musteri hub'ina girmez.
 *
 * Sahip hesabinda kitaplik, abonelik veya cihaz yok; bu sayfalar onun icin
 * kalici olarak bos. Adres elle yazilsa bile konsola geri gonderilir — "Hub'a
 * don" ile bos ekranlar arasinda dolasmak, konsolun kendi yuzeyi olmadigi
 * izlenimi veriyordu (kullanici geri bildirimi, 19 Agustos 2026).
 *
 * Hesap ayarlari (/hesap) ISTISNA: sahibin de parolasi ve temasi var.
 */
function CustomerOnly({ children }: { children: ReactNode }) {
  const { can } = useAuth();
  if (can('admin')) return <Navigate to="/yonetim" replace />;
  return <>{children}</>;
}

/** Girisli kullanici giris ekranini gormemeli. Yonetim yetkili hesabin evi
    konsol: business hesabi hub'a degil dogrudan /yonetim'e iner. */
function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { me, loading, can } = useAuth();
  if (loading) return null;
  if (me) return <Navigate to={can('admin') ? '/yonetim' : '/'} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<div style={{ padding: 'var(--space-7)', maxWidth: 720, margin: '0 auto' }}><LoadingBlock rows={4} label="Sayfa yükleniyor" /></div>}>
    <Routes>
      {/* ── Kimlik ─────────────────────────────────────────────────────────── */}
      <Route element={<AuthLayout />}>
        <Route path="/personel-daveti" element={<WorkforceInvitation />} />
        <Route
          path="/giris"
          element={
            <RedirectIfAuthed>
              <Login />
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/kayit"
          element={
            <RedirectIfAuthed>
              <Register />
            </RedirectIfAuthed>
          }
        />
        <Route path="/parola-sifirla" element={<ForgotPassword />} />
        <Route path="/parola-yenile" element={<ResetPassword />} />
        <Route path="/eposta-dogrula" element={<VerifyEmail />} />
        <Route path="/google-onay" element={<GoogleConsent />} />
        <Route path="/kvkk-aydinlatma" element={<LegalPage />} />
        <Route path="/kullanim-kosullari" element={<LegalPage />} />
        <Route path="/davet" element={<Invitation />} />
      </Route>

      <Route path="/demo/led/:token" element={<LedDemo />} />

      {/* ── Uygulama ───────────────────────────────────────────────────────── */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Musteri yuzeyi — yonetici buraya girmez, konsola doner. */}
        <Route element={<CustomerOnly><Outlet /></CustomerOnly>}>
          <Route path="/" element={<Home />} />
          <Route path="/kesfet" element={<Library />} />
          <Route path="/kesfet/:slug" element={<ProductDetail />} />
          <Route path="/teklif-al" element={<QuoteRequest />} />
          <Route path="/paket-bulucu" element={<PackageFinder />} />
          {/* Eski baglantilar kirilmaz; ayri Iceriklerim yuzeyi kaldirildi,
              urunler katalog ailelerinde gorulur. */}
          <Route path="/iceriklerim" element={<Navigate to="/kesfet" replace />} />
          <Route path="/iceriklerim/:slug" element={<LegacyOwnedContentRedirect />} />
          {/* Kullaniciya tanimli katalogun CALISMA ALANI — kesfetin filtresi
              degil, kendi kimliginde sayfa (kullanici karari, 19 Agu). */}
          <Route path="/katalog/:kategori" element={<CategoryPage />} />
          <Route path="/abonelikler" element={<Subscriptions />} />
          <Route path="/abonelikler/teklifler/:id" element={<OfferDetail />} />
          {/* Eski kimlik: /kitaplik kaliciydi, sayfa Kesfet'e donustu (19 Agu).
              Panolara kaydedilmis baglantilar ve eski banner CTA'lari kirilmasin. */}
          <Route path="/kitaplik" element={<LegacyLibraryRedirect />} />
          <Route path="/kitaplik/:slug" element={<LegacyLibraryRedirect />} />

          <Route path="/operasyon" element={<Navigate to="/operasyon/ekranlar" replace />} />
          <Route
            path="/operasyon/ekranlar"
            element={
              <RequireNav nav="operations">
                <Endpoints />
              </RequireNav>
            }
          />
          <Route
            path="/operasyon/takvim"
            element={
              <RequireNav nav="operations">
                <Schedule />
              </RequireNav>
            }
          />
          <Route
            path="/operasyon/oturumlar"
            element={
              <RequireNav nav="operations">
                <Schedule />
              </RequireNav>
            }
          />
          <Route path="/raporlar" element={<RequireNav nav="operations"><Reports /></RequireNav>} />
          <Route path="/operasyon/led-reklamlari" element={<RequireNav nav="operations"><LedOperations /></RequireNav>} />
          <Route path="/operasyon/ekran-studyo" element={<RequireNav nav="operations"><DisplayRuntimeWorkspace audience="customer" /></RequireNav>} />
          <Route path="/destek" element={<Support />} />
          <Route path="/takvim/randevu" element={<CalendarBooking />} />
        </Route>

        <Route path="/hesap/*" element={<Account />} />

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Yonetim konsolu: musteri kabugundan AYRI yuzey ─────────────────── */}
      <Route
        path="/yonetim"
        element={
          <RequireAuth>
            <RequireNav nav="admin">
              <AdminShell />
            </RequireNav>
          </RequireAuth>
        }
      >
        <Route index element={<Overview />} />
        <Route path="is-takibi" element={<AdminTasks />} />
        <Route path="musteriler" element={<Accounts />} />
        <Route path="musteriler/yeni" element={<NewCustomerPage />} />
        <Route path="musteriler/:id" element={<AccountDetailPage />} />
        <Route path="musteriler/:id/teklifler/:offerId" element={<CustomerOfferPage />} />
        <Route path="musteriler/:id/etkinlikler/:eventId" element={<CustomerEventEditor />} />
        <Route path="aktivasyon" element={<LegacyActivationRedirect />} />

        <Route path="icerik" element={<ContentCenter />} />
        <Route path="icerik/urunler" element={<Products />} />
        <Route path="icerik/urunler/:id" element={<Products />} />
        <Route path="icerik/kategoriler" element={<CategoryContent />} />
        <Route path="icerik/medya" element={<MediaLibrary />} />
        <Route path="icerik/bannerlar" element={<Banners />} />

        <Route path="ticari" element={<Navigate to="/yonetim/ticari/teklifler" replace />} />
        <Route path="ticari/paketler" element={<Packages />} />
        <Route path="ticari/teklifler" element={<QuoteRequests />} />
        <Route path="ticari/ozel-teklifler" element={<AdminOffers />} />
        <Route path="ticari/faturalar" element={<AdminInvoices />} />

        <Route path="operasyon" element={<OperationsCenter />} />
        <Route path="operasyon/envanter" element={<AdminInventory />} />
        <Route path="operasyon/oturumlar" element={<OperationsSessions />} />
        <Route path="operasyon/takvim" element={<Navigate to="/yonetim/operasyon" replace />} />
        <Route path="operasyon/led" element={<Navigate to="/yonetim/operasyon" replace />} />
        <Route path="operasyon/ekran-studyo" element={<Navigate to="/yonetim/operasyon" replace />} />
        <Route path="website" element={<AdminWebsite />} />
        <Route path="website/medya" element={<MediaLibrary />} />
        <Route path="website/blog" element={<AdminWebsite initialPageType="blog" />} />
        <Route path="website/sayfalar/:id" element={<AdminWebsiteEditor />} />
        <Route path="yayinlar" element={<ReleasesCenter />} />
        <Route path="destek" element={<SupportCenter />} />
        <Route path="sistem" element={<SystemCenter />} />
        <Route path="sistem/gelistirici-erisimi" element={<AdminDevAccess />} />
        <Route path="sistem/personel" element={<AdminWorkforce />} />
        <Route path="sistem/roller" element={<AdminStaffRoles />} />
        <Route path="sistem/denetim" element={<Audit />} />
        <Route path="sistem/eposta" element={<AdminEmails />} />

        {/* Eski sık kullanılan yönetim adresleri yeni bilgi mimarisine taşınır. */}
        <Route path="hesaplar" element={<Navigate to="/yonetim/musteriler" replace />} />
        <Route path="urunler" element={<Navigate to="/yonetim/icerik/urunler" replace />} />
        <Route path="paketler" element={<Navigate to="/yonetim/ticari/paketler" replace />} />
        <Route path="banner" element={<Navigate to="/yonetim/icerik/bannerlar" replace />} />
        <Route path="kategoriler" element={<Navigate to="/yonetim/icerik/kategoriler" replace />} />
        <Route path="denetim" element={<Navigate to="/yonetim/sistem/denetim" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
